import React, { useEffect, useState } from 'react';
import { devCost, fmtAED } from '../utils';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { projectsAPI, productsAPI, settingsAPI } from '../api';
import ProjectReport from './ProjectReport';
import ProjectSummary from './ProjectSummary';
import ProjectMaterialReport from './ProjectMaterialReport';

const STATUS_LABELS = {
  trial: 'Trial', recipe_confirmed: 'Recipe Confirmed',
  commercial_trial: 'Commercial Trial', launched: 'Launched',
};

function calcDuration(start, end) {
  if (!start) return null;
  const s = new Date(start);
  const e = end ? new Date(end) : new Date();
  const days = Math.floor((e - s) / (1000*60*60*24));
  if (days < 0) return null;
  const months = Math.floor(days/30);
  const remDays = days%30;
  if (months===0) return days+' days';
  if (remDays===0) return months+' month'+(months>1?'s':'');
  return months+' month'+(months>1?'s':'')+' '+remDays+' day'+(remDays>1?'s':'');
}

const AUTO_PALETTE = [['#ede7f6','#5e35b1'],['#e0f2f1','#00695c'],['#fff8e1','#ef6c00'],['#e1f5fe','#0277bd'],['#fce4ec','#ad1457'],['#f1f8e9','#558b2f'],['#efebe9','#4e342e']];
const autoStatusStyle = (s) => { let h = 0; for (const ch of String(s || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0; const [bg, color] = AUTO_PALETTE[h % AUTO_PALETTE.length]; return { bg, color }; };

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [products, setProducts] = useState([]);
  const [filterSfg, setFilterSfg] = useState('');
  const [viewMode, setViewMode] = useState('tile');
  const [depts, setDepts] = useState([]);
  const [productStatuses, setProductStatuses] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [projectStatuses, setProjectStatuses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('products');
  const [showEditProject, setShowEditProject] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [form, setForm] = useState({ name: '', department: '', internal_group: '', product_type: '', status: 'trial', description: '', category: '', sub_category: '' });
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);

  const load = () => {
    projectsAPI.get(id).then(r => {
      setProject(r.data);
      setEditForm({ project_code: r.data.project_code, name: r.data.name, description: r.data.description||'', department: r.data.department||'', status: r.data.status, start_date: r.data.start_date||'', end_date: r.data.end_date||'' });
    });
    productsAPI.getAll({ project_id: id }).then(r => setProducts(r.data));
    settingsAPI.getType('department').then(r => setDepts(r.data));
    settingsAPI.getType('product_status').then(r => setProductStatuses(r.data));
    settingsAPI.getType('product_type').then(r => setProductTypes(r.data));
    settingsAPI.getType('project_status').then(r => setProjectStatuses(r.data));
    settingsAPI.getType('category').then(r => setCategories(r.data));
    settingsAPI.getType('sub_category').then(r => setSubCategories(r.data));
  };
  useEffect(() => { load(); }, [id]);

  const submit = async () => {
    if (!form.name || !form.department || !form.internal_group)
      return toast.error('Code, name, department and group are required');
    try {
      await productsAPI.create({ ...form, project_id: id });
      toast.success('Product added');
      setShowModal(false);
      setForm({ name: '', department: '', internal_group: '', product_type: '', status: 'trial', description: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
  };

  if (!project) return <div className="empty-state"><p>Loading...</p></div>;

  const totalCost = products.reduce((s, p) => s + devCost(p), 0);

  return (
    <div>
      <div className="breadcrumb">
        <Link to="/projects">Projects</Link>
        <span className="breadcrumb-sep">›</span>
        <span>{project.name}</span>
      </div>

      <div className="page-header">
        <div>
          <div className="page-title">{project.name}</div>
          <div className="page-subtitle">
            <span className="mono">{project.project_code}</span>
            {project.department && <span> · {project.department}</span>}
            {project.start_date && <span> · {project.start_date} → {project.end_date || 'Ongoing'}</span>}
            {calcDuration(project.start_date, project.end_date) && <span style={{ color: 'var(--accent)', marginLeft: 6 }}>({calcDuration(project.start_date, project.end_date)})</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <span className={`badge badge-${project.status}`}>{project.status}</span>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowEditProject(true)}>✏ Edit Project</button>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Add Product</button>
        </div>
      </div>

      <div className="stats-grid mb-6">
        <div className="stat-card"><div className="stat-label">Products</div><div className="stat-value">{products.length}</div></div>
        <div className="stat-card amber"><div className="stat-label">In Trial</div><div className="stat-value">{products.filter(p => p.status === 'trial').length}</div></div>
        <div className="stat-card"><div className="stat-label">Launched</div><div className="stat-value">{products.filter(p => p.status === 'launched').length}</div></div>
        <div className="stat-card blue">
          <div className="stat-label">Total R&D Cost</div>
          <div className="stat-value" style={{ fontSize: 18 }}>AED {totalCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
        </div>
      </div>

      <div className="tabs">
        <button className={`tab ${activeTab === 'products' ? 'active' : ''}`} onClick={() => setActiveTab('products')}>Products</button>
        <button className={`tab ${activeTab === 'report' ? 'active' : ''}`} onClick={() => setActiveTab('report')}>Project Cost Report</button>
        <button className={`tab ${activeTab === 'summary' ? 'active' : ''}`} onClick={() => setActiveTab('summary')}>Project Summary</button>
        <button className={`tab ${activeTab === 'materials' ? 'active' : ''}`} onClick={() => setActiveTab('materials')}>Material Report</button>
      </div>

      {activeTab === 'report' && <ProjectReport project={project} />}
      {activeTab === 'summary' && <ProjectSummary project={project} statuses={productStatuses} />}
      {activeTab === 'materials' && <ProjectMaterialReport project={project} />}

      {activeTab === 'products' && (
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div className="card-title" style={{ margin: 0 }}>Products</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <div style={{ display: 'flex', gap: 4, background: 'var(--bg-elevated)', borderRadius: 8, padding: 3 }}>
              {[['', 'All'], ['fg', 'FG'], ['sfg', 'SFG']].map(([val, label]) => (
                <button key={val} className={'btn btn-sm ' + (filterSfg === val ? 'btn-primary' : 'btn-ghost')}
                  onClick={() => setFilterSfg(val)} style={{ padding: '4px 10px', fontSize: 12 }}>{label}</button>
              ))}
            </div>
            <button onClick={() => setViewMode('tile')} className={`btn btn-sm ${viewMode==='tile' ? 'btn-primary' : 'btn-secondary'}`}>⊞</button>
            <button onClick={() => setViewMode('list')} className={`btn btn-sm ${viewMode==='list' ? 'btn-primary' : 'btn-secondary'}`}>☰</button>
          </div>
        </div>
        {products.length === 0 ? (
          <div className="empty-state"><div className="empty-state-icon">◈</div><p>No products yet.</p></div>
        ) : (() => {
          const filtered = products.filter(p => !filterSfg || (filterSfg==='sfg'?p.is_sfg===1:p.is_sfg!==1));
          if (viewMode === 'list') return (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Code</th><th>Name</th><th>Dept</th><th>Group</th><th>Type</th><th>Status</th><th>Recipe</th><th className="text-right">Cost</th></tr>
                </thead>
                <tbody>
                  {filtered.map(p => (
                    <tr key={p.id} onClick={() => navigate(`/products/${p.id}`)} style={{ cursor: 'pointer' }}>
                      <td className="mono">{p.product_code}</td>
                      <td style={{ fontWeight: 500 }}>{p.name}</td>
                      <td>{p.department}</td>
                      <td className="mono">{p.internal_group}</td>
                      <td className="text-muted">{p.product_type || '—'}</td>
                      <td><span className={`badge badge-${p.status}`} style={{ ...(STATUS_LABELS[p.status] ? {} : { background: autoStatusStyle(p.status).bg, color: autoStatusStyle(p.status).color }), whiteSpace: 'normal', display: 'inline-block', maxWidth: 150, lineHeight: '1.25', textAlign: 'center' }}>{STATUS_LABELS[p.status] || p.status}</span></td>
                      <td>{p.recipe_confirmed ? <span className="text-accent" style={{ fontSize: 12 }}>✓</span> : <span className="text-muted" style={{ fontSize: 12 }}>—</span>}</td>
                      <td className="text-right cost-value">{devCost(p) > 0 ? `AED ${devCost(p).toLocaleString('en-AE', { maximumFractionDigits: 0 })}` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
          return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
              {filtered.map(p => (
                <div key={p.id} onClick={() => navigate(`/products/${p.id}`)}
                  style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px', cursor: 'pointer', transition: 'all 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor='var(--accent)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor='var(--border)'}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <span className="mono" style={{ fontSize: 10, color: 'var(--accent)', fontWeight: 700 }}>{p.product_code}</span>
                    <span className={`badge badge-${p.status}`} style={{ ...(STATUS_LABELS[p.status] ? {} : { background: autoStatusStyle(p.status).bg, color: autoStatusStyle(p.status).color }), whiteSpace: 'normal', display: 'inline-block', maxWidth: 150, lineHeight: '1.25', textAlign: 'center' }}>{STATUS_LABELS[p.status] || p.status}</span>
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{p.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>{p.department} · {p.product_type || '—'}</div>
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <div style={{ fontSize: 11 }}>{p.is_sfg ? <span style={{ color: 'var(--text-muted)' }}>SFG</span> : <span style={{ color: 'var(--accent)' }}>FG</span>} {p.recipe_confirmed ? '· ✓ Recipe' : ''}</div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--amber)' }}>{devCost(p) > 0 ? `AED ${devCost(p).toLocaleString('en-AE', { maximumFractionDigits: 0 })} Dev` : '—'}</div>
                    </div>
                    {p.recipe_cost > 0 && p.recipe_batch_kg > 0 && (
                      <div style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600 }}>
                        AED {(p.recipe_cost / p.recipe_batch_kg).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/kg
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Add Product</div>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="form-grid">

              <div className="form-group">
                <label>Product Name *</label>
                <input placeholder="e.g. Mango Snack" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Department *</label>
                <select value={form.department} onChange={e => setForm({ ...form, department: e.target.value })}>
                  <option value="">Select department</option>
                  {depts.map(d => <option key={d.id}>{d.value}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Internal Group *</label>
                <input placeholder="e.g. SNACKS, GRP-A" value={form.internal_group} onChange={e => setForm({ ...form, internal_group: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Product Type</label>
                <select value={form.product_type} onChange={e => setForm({ ...form, product_type: e.target.value })}>
                  <option value="">Select type</option>
                  {productTypes.map(t => <option key={t.id}>{t.value}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Category *</label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} required>
                  <option value="">Select category</option>
                  {categories.map(c => <option key={c.id}>{c.value}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Sub Category</label>
                <select value={form.sub_category} onChange={e => setForm({ ...form, sub_category: e.target.value })}>
                  <option value="">Select sub category</option>
                  {subCategories.filter(s => true).map(c => <option key={c.id}>{c.value}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  {productStatuses.map(s => <option key={s.id} value={s.value}>{STATUS_LABELS[s.value] || s.value}</option>)}
                </select>
              </div>
              <div className="form-group full" style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 8 }}>
                <input type="checkbox" id="has_sfg_new" checked={!!form.has_sfg} onChange={e => setForm({ ...form, has_sfg: e.target.checked ? 1 : 0 })} style={{ width: 18, height: 18 }} />
                <label htmlFor="has_sfg_new" style={{ margin: 0, cursor: 'pointer' }}>This product has an SFG (Semi-Finished Good)</label>
              </div>
              <div className="form-group full">
                <label>Description</label>
                <textarea placeholder="Product description..." value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={submit}>Add Product</button>
            </div>
          </div>
        </div>
      )}
    {showEditProject && (
      <div className="modal-overlay" onClick={() => setShowEditProject(false)}>
        <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-title">Edit Project</div>
            <button className="modal-close" onClick={() => setShowEditProject(false)}>×</button>
          </div>
          <div className="form-grid">
            <div className="form-group"><label>Project Code</label><input value={editForm.project_code} onChange={e => setEditForm({...editForm, project_code: e.target.value})} /></div>
            <div className="form-group"><label>Project Name</label><input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} /></div>
            <div className="form-group">
              <label>Department</label>
              <select value={editForm.department} onChange={e => setEditForm({...editForm, department: e.target.value})}>
                <option value="">Select department</option>
                {depts.map(d => <option key={d.id}>{d.value}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select value={editForm.status} onChange={e => setEditForm({...editForm, status: e.target.value})}>
                {(projectStatuses||[]).map(s => <option key={s.id} value={s.value}>{s.value}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Start Date</label><input type="date" value={editForm.start_date} onChange={e => setEditForm({...editForm, start_date: e.target.value})} /></div>
            <div className="form-group"><label>End Date (Target)</label><input type="date" value={editForm.end_date} onChange={e => setEditForm({...editForm, end_date: e.target.value})} /></div>
            <div className="form-group full"><label>Description</label><textarea value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} /></div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={() => setShowEditProject(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={async () => {
              await projectsAPI.update(id, editForm);
              toast.success('Project updated');
              setShowEditProject(false);
              load();
            }}>Save Changes</button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
}
